import { useEffect, useRef } from 'react';
import { useToast } from '@wistia/vhs';
import type { Nullable } from '~/utilities/type-guards';
import { isNil } from '~/utilities/type-guards';
import { useSearchParams } from '~/hooks/useSearchParams';
import { useIsAnonymousRecordWorkflow } from './useIsAnonymousRecordWorkflow';
import { triggerOnMediaContentChanged } from '../mediaContentChanged';
import type { AnonymousRecordingUploadStatus } from '../../WelcomeScreen/hooks/useSendAnonymousRecordings';

/**
 * If we are in the anonymous record flow, sometimes it takes longer for the granary to process
 * the uploaded files, by the time users arrive to the project page, the media files are not ready.
 * This hook will keep pulling the media files until they are ready, but only in the record flow.
 */

export type Project = {
  medias: {
    totalCount: number;
  };
};

export const FIRST_MEDIA_LOADED_ACTION = 'FIRST_MEDIA_LOADED';

const INTERVAL = 5000;

export const usePollingMedia = (project: Project): void => {
  const isAnonymousWorkflow = useIsAnonymousRecordWorkflow();
  const initialCount = useRef<number>(0);
  const [searchParams] = useSearchParams();
  const timerId = useRef<NodeJS.Timeout | null>(null);
  const showToast = useToast();

  useEffect(() => {
    if (isAnonymousWorkflow && searchParams instanceof URLSearchParams) {
      const status: Nullable<AnonymousRecordingUploadStatus> = searchParams.get(
        'status',
      ) as AnonymousRecordingUploadStatus;
      const message =
        status === 'failure'
          ? 'We could not retrieve your recording. We only hold recordings for 24 hours.'
          : `Once processing is done, you'll be able to edit and share.`;

      showToast({
        message,
        timeout: 10000,
        variant: status === 'failure' ? 'error' : 'success',
      });
    }
  }, [isAnonymousWorkflow, searchParams, showToast]);

  useEffect(() => {
    if (isAnonymousWorkflow && !isNil(project) && isNil(timerId.current)) {
      const intervalId = setInterval(triggerOnMediaContentChanged, INTERVAL);
      timerId.current = intervalId;
      initialCount.current = project.medias.totalCount;
    }

    if (
      !isNil(project) &&
      !isNil(timerId.current) &&
      project.medias.totalCount > initialCount.current
    ) {
      window.postMessage({ action: FIRST_MEDIA_LOADED_ACTION }, window.origin);
      clearInterval(timerId.current);
    }

    return () => {
      if (!isNil(timerId.current)) {
        clearInterval(timerId.current);
      }
    };
  }, [isAnonymousWorkflow, searchParams, timerId, initialCount, project]);
};
